import { graphqlQuery } from "./graphqlClient";
// --- GraphQL Queries ---
const FIELD_SURVEY_LIST_QUERY = `
  query fieldSurveyRequests(
    $status: String, $internalId: String, $handlingType: String,
    $candidateId: String, $storeNo: String, $storeName: String,
    $prefecture: String, $contractType: String, $requestCount: Int,
    $submissionMethod: String, $designOffice: String,
    $page: Int, $perPage: Int
  ) {
    fieldSurveyRequests(
      status: $status, internalId: $internalId, handlingType: $handlingType,
      candidateId: $candidateId, storeNo: $storeNo, storeName: $storeName,
      prefecture: $prefecture, contractType: $contractType, requestCount: $requestCount,
      submissionMethod: $submissionMethod, designOffice: $designOffice,
      page: $page, perPage: $perPage
    ) {
      totalCount
      page
      perPage
      items {
        requestId
        statusCode
        statusName
        internalId
        handlingTypeCode
        handlingTypeName
        candidateId
        prefectureCode
        prefectureName
        storeNo
        storeName
        contractTypeCode
        contractTypeName
        requestCount
        submissionMethodCode
        submissionMethodName
        designOffice
        boxLink
        approvalBoxLink
        reportBoxLink
      }
    }
  }
`;
const FIELD_SURVEY_DETAIL_QUERY = `
  query fieldSurveyRequestDetail($internalId: String!) {
    fieldSurveyRequestDetail(internalId: $internalId) {
      requestId
      statusCode
      statusName
      internalId
      requestCount
      requestDate
      handlingTypeCode
      handlingTypeName
      candidateId
      storeNo
      storeName
      storeNameKana
      prefectureCode
      prefectureName
      zo
      zoName
      do
      doName
      address
      contractTypeCode
      contractTypeName
      ownershipCode
      ownershipName
      buildingTypeCode
      buildingTypeName
      licenseCode
      licenseName
      openDate
      surveyDesiredDate
      reportDueDate
      submissionMethodCode
      submissionMethodName
      confirmation
      retryReason
      remarks
      requesterCode
      requesterName
      approverCode
      approverName
      dmCode
      dmName
      designOfficeCode
      designOfficeName
      approvalDept
      approvalArch
      boxLink
      approvalBoxLink
      reportBoxLink
      documents {
        documentType
        documentName
        fileName
        uploader
        uploadDate
        gcsPath
      }
    }
  }
`;
const GENERATE_INTERNAL_ID_QUERY = `
  query generateFieldSurveyInternalId {
    generateFieldSurveyInternalId {
      internalId
    }
  }
`;
const INIT_NEW_REQUEST_QUERY = `
  query initNewRequest {
    generateFieldSurveyInternalId {
      internalId
    }
    myDepartmentManager {
      employeeCode
      employeeName
    }
  }
`;
const STORE_INFO_QUERY = `
  query storeInfo($storeNo: String!) {
    storeInfo(storeNo: $storeNo) {
      storeNo
      storeName
      storeNameKana
      prefectureCode
      prefectureName
      zo
      do
      address
      contractTypeCode
      contractTypeName
      ownershipCode
      ownershipName
      dmCode
      dmName
    }
  }
`;
const EMPLOYEE_SEARCH_QUERY = `
  query employeeSearch($input: EmployeeSearchInput) {
    employeeSearch(input: $input) {
      totalCount
      items {
        employeeCode
        employeeName
        departmentName
        position
        hasApprovalAuthority
        hasDmAuthority
      }
    }
  }
`;
const PARTNER_SEARCH_QUERY = `
  query partnerSearch($input: PartnerSearchInput) {
    partnerSearch(input: $input) {
      totalCount
      items {
        partnerCode
        partnerName
        partnerType
      }
    }
  }
`;
// --- GraphQL API Functions ---
export async function fetchFieldSurveyRequests(variables) {
    const data = await graphqlQuery(FIELD_SURVEY_LIST_QUERY, variables);
    return data.fieldSurveyRequests;
}
export async function fetchFieldSurveyRequestDetail(internalId) {
    const data = await graphqlQuery(FIELD_SURVEY_DETAIL_QUERY, { internalId });
    return data.fieldSurveyRequestDetail;
}
export async function generateFieldSurveyInternalId() {
    const data = await graphqlQuery(GENERATE_INTERNAL_ID_QUERY);
    return data.generateFieldSurveyInternalId.internalId;
}
export async function initNewRequest() {
    const data = await graphqlQuery(INIT_NEW_REQUEST_QUERY);
    return {
        internalId: data.generateFieldSurveyInternalId.internalId,
        manager: data.myDepartmentManager,
    };
}
export async function fetchStoreInfo(storeNo) {
    const data = await graphqlQuery(STORE_INFO_QUERY, { storeNo });
    return data.storeInfo;
}
export async function searchEmployees(conditions) {
    const input = {};
    if (conditions.searchType)
        input.searchType = conditions.searchType;
    if (conditions.departmentName)
        input.departmentName = conditions.departmentName;
    if (conditions.employeeName)
        input.employeeName = conditions.employeeName;
    if (conditions.position)
        input.position = conditions.position;
    const data = await graphqlQuery(EMPLOYEE_SEARCH_QUERY, { input });
    return data.employeeSearch.items;
}
export async function searchPartners(conditions) {
    const input = {};
    if (conditions.partnerName)
        input.partnerName = conditions.partnerName;
    if (conditions.partnerType)
        input.partnerType = conditions.partnerType;
    const data = await graphqlQuery(PARTNER_SEARCH_QUERY, { input });
    return data.partnerSearch.items;
}
const FIELD_SURVEY_STATUS_QUERY = `
  query fieldSurveyRequestStatus($internalId: String!) {
    fieldSurveyRequestStatus(internalId: $internalId) {
      statusCode
      statusName
    }
  }
`;
export async function fetchFieldSurveyRequestStatus(internalId) {
    const data = await graphqlQuery(FIELD_SURVEY_STATUS_QUERY, { internalId });
    return data.fieldSurveyRequestStatus;
}
// --- REST API Functions ---
/**
 * サーバーはHTTP 200のままボディのstatusCodeでエラーを返すため、
 * statusCodeが200以外の場合はApiResponseErrorとして扱う。
 * 4xx（業務エラー）のmessageは画面表示用の文言、5xx（システムエラー）のmessageは内部情報を含むため画面に出さない。
 */
export class ApiResponseError extends Error {
    statusCode;
    constructor(statusCode, message) {
        super(message);
        this.name = 'ApiResponseError';
        this.statusCode = statusCode;
    }
    get isBusinessError() {
        return this.statusCode >= 400 && this.statusCode < 500;
    }
}
/** 画面に表示するエラー文言を返す。業務エラー(4xx)はサーバーのメッセージ、それ以外は引数の固定文 */
export function getApiErrorMessage(error, fallback) {
    if (error instanceof ApiResponseError && error.isBusinessError && error.message) {
        return error.message;
    }
    return fallback;
}
// モック: 通信せず成功を返す（状態は保持しない）
export async function registerFieldSurveyRequest(_payload) {
    return {};
}
export async function updateFieldSurveyRequest(_payload) { }
export async function cancelFieldSurveyRequest(_payload) { }
export async function approveFieldSurveyRequest(_payload) { }
export async function rejectFieldSurveyRequest(_payload) { }
export async function retryFieldSurveyRequest(_payload) {
    return {};
}
